import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminSiteSettingsService } from '../../../services/adminSiteSettingsService';
import { Save, Globe, Mail, Phone, MapPin, Image, FileText, Share2, Code, Eye, Upload, Plus, Trash2, Info } from 'lucide-react';
import { Button } from '../../shared';
import { useToastContext } from '../../../contexts/ToastContext';

const SiteSettings = () => {
  const queryClient = useQueryClient();
  const toast = useToastContext();

  const [formData, setFormData] = useState({
    site_name: '',
    site_email: '',
    site_phone: '',
    site_address: '',
    site_logo: '',
    site_favicon: '',
    footer_tagline: '',
    footer_copyright: '',
    meta_description: '',
    meta_keywords: '',
    google_analytics_id: '',
    facebook_pixel_id: '',
    custom_css: '',
    custom_js: '',
    about_hero_image: '',
    about_subtitle: '',
    about_title: '',
    about_content: '',
    about_cta_text: '',
    about_cta_link: '',
  });
  const [socialLinks, setSocialLinks] = useState([]);

  // Fetch site settings
  const { data: settingsResponse, isLoading } = useQuery({
    queryKey: ['adminSiteSettings'],
    queryFn: () => adminSiteSettingsService.get(),
  });

  React.useEffect(() => {
    if (settingsResponse?.data) {
      const settings = settingsResponse.data;
      setFormData({
        site_name: settings.site_name || '',
        site_email: settings.site_email || '',
        site_phone: settings.site_phone || '',
        site_address: settings.site_address || '',
        site_logo: settings.site_logo || '',
        site_favicon: settings.site_favicon || '',
        footer_tagline: settings.footer_tagline || '',
        footer_copyright: settings.footer_copyright || '',
        meta_description: settings.meta_description || '',
        meta_keywords: settings.meta_keywords || '',
        google_analytics_id: settings.google_analytics_id || '',
        facebook_pixel_id: settings.facebook_pixel_id || '',
        custom_css: settings.custom_css || '',
        custom_js: settings.custom_js || '',
        about_hero_image: settings.about_hero_image || '',
        about_subtitle: settings.about_subtitle || '',
        about_title: settings.about_title || '',
        about_content: settings.about_content || '',
        about_cta_text: settings.about_cta_text || '',
        about_cta_link: settings.about_cta_link || '',
      });
      if (settings.social_links && Array.isArray(settings.social_links) && settings.social_links.length > 0) {
        setSocialLinks(settings.social_links.map((l, i) => ({ id: `sl-${i}-${Date.now()}`, platform: l.platform || '', url: l.url || '' })));
      } else {
        const legacyMap = [
          { key: 'facebook_url', platform: 'Facebook' }, { key: 'twitter_url', platform: 'Twitter' },
          { key: 'instagram_url', platform: 'Instagram' }, { key: 'linkedin_url', platform: 'LinkedIn' },
          { key: 'youtube_url', platform: 'YouTube' }, { key: 'tiktok_url', platform: 'TikTok' },
          { key: 'pinterest_url', platform: 'Pinterest' },
        ];
        const legacy = legacyMap.filter((l) => settings[l.key]).map((l, i) => ({ id: `sl-legacy-${i}`, platform: l.platform, url: settings[l.key] }));
        setSocialLinks(legacy.length > 0 ? legacy : [{ id: 'sl-0', platform: '', url: '' }]);
      }
    }
  }, [settingsResponse]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (data) => adminSiteSettingsService.save(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['adminSiteSettings']);
      queryClient.invalidateQueries(['siteSettings']);
      toast.success(null, 'Site settings saved successfully');
    },
    onError: (error) => {
      toast.error(null, error.response?.data?.message || 'Failed to save site settings');
    },
  });

  const [logoUploading, setLogoUploading] = useState(false);
  const [faviconUploading, setFaviconUploading] = useState(false);
  const [aboutHeroUploading, setAboutHeroUploading] = useState(false);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const res = await adminSiteSettingsService.uploadLogo(file);
      if (res?.data?.url) {
        setFormData((prev) => ({ ...prev, site_logo: res.data.url }));
        toast.success(null, 'Logo uploaded to S3');
      }
    } catch (err) {
      toast.error(null, err?.response?.data?.message || 'Failed to upload logo');
    } finally {
      setLogoUploading(false);
      e.target.value = '';
    }
  };

  const handleFaviconUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFaviconUploading(true);
    try {
      const res = await adminSiteSettingsService.uploadFavicon(file);
      if (res?.data?.url) {
        setFormData((prev) => ({ ...prev, site_favicon: res.data.url }));
        toast.success(null, 'Favicon uploaded to S3');
      }
    } catch (err) {
      toast.error(null, err?.response?.data?.message || 'Failed to upload favicon');
    } finally {
      setFaviconUploading(false);
      e.target.value = '';
    }
  };

  const handleAboutHeroUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAboutHeroUploading(true);
    try {
      const res = await adminSiteSettingsService.uploadAboutHero(file);
      if (res?.data?.url) {
        setFormData((prev) => ({ ...prev, about_hero_image: res.data.url }));
        toast.success(null, 'Hero image uploaded');
      }
    } catch (err) {
      toast.error(null, err?.response?.data?.message || 'Failed to upload image');
    } finally {
      setAboutHeroUploading(false);
      e.target.value = '';
    }
  };

  const addSocialLink = () => {
    setSocialLinks((prev) => [...prev, { id: `sl-${Date.now()}`, platform: '', url: '' }]);
  };

  const removeSocialLink = (id) => {
    setSocialLinks((prev) => prev.filter((l) => l.id !== id));
  };

  const updateSocialLink = (id, field, value) => {
    setSocialLinks((prev) => prev.map((l) => (l.id === id ? { ...l, [field]: value } : l)));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const links = socialLinks.filter((l) => l.platform?.trim() && l.url?.trim()).map(({ platform, url }) => ({ platform: platform.trim(), url: url.trim() }));
    saveMutation.mutate({ ...formData, social_links: links });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <form onSubmit={handleSubmit} className="p-6 space-y-8">
        {/* Basic Information */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Globe className="w-5 h-5" />
            Basic Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Site Name *
              </label>
              <input
                type="text"
                required
                value={formData.site_name}
                onChange={(e) => setFormData({ ...formData, site_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                placeholder="Your Site Name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Site Email *
              </label>
              <input
                type="email"
                required
                value={formData.site_email}
                onChange={(e) => setFormData({ ...formData, site_email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                placeholder="contact@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Phone
              </label>
              <input
                type="tel"
                value={formData.site_phone}
                onChange={(e) => setFormData({ ...formData, site_phone: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                placeholder="+1 (555) 123-4567"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address
              </label>
              <input
                type="text"
                value={formData.site_address}
                onChange={(e) => setFormData({ ...formData, site_address: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                placeholder="123 Main St, City, State, ZIP"
              />
            </div>
          </div>
        </div>

        {/* Logo & Favicon - Upload to S3 */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Image className="w-5 h-5" />
            Branding
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Site Logo (upload to S3)
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  disabled={logoUploading}
                  className="hidden"
                  id="site-logo-upload"
                />
                <label htmlFor="site-logo-upload" className="cursor-pointer flex flex-col items-center">
                  <Upload className="w-8 h-8 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600">
                    {logoUploading ? 'Uploading...' : 'Click to upload logo'}
                  </span>
                  <span className="text-xs text-gray-500 mt-1">PNG, JPG, GIF, WebP, SVG (max 5MB)</span>
                </label>
              </div>
              {formData.site_logo && (
                <div className="mt-2 flex items-center gap-2">
                  <img src={formData.site_logo} alt="Logo preview" className="h-16 object-contain" />
                  <button
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, site_logo: '' }))}
                    className="text-xs text-red-600 hover:underline"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Favicon (upload to S3)
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                <input
                  type="file"
                  accept="image/*,.ico"
                  onChange={handleFaviconUpload}
                  disabled={faviconUploading}
                  className="hidden"
                  id="site-favicon-upload"
                />
                <label htmlFor="site-favicon-upload" className="cursor-pointer flex flex-col items-center">
                  <Upload className="w-8 h-8 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600">
                    {faviconUploading ? 'Uploading...' : 'Click to upload favicon'}
                  </span>
                  <span className="text-xs text-gray-500 mt-1">ICO, PNG, JPG (max 2MB)</span>
                </label>
              </div>
              {formData.site_favicon && (
                <div className="mt-2 flex items-center gap-2">
                  <img src={formData.site_favicon} alt="Favicon preview" className="h-8 w-8 object-contain" />
                  <button
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, site_favicon: '' }))}
                    className="text-xs text-red-600 hover:underline"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* About Page */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Info className="w-5 h-5" />
            About Page
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Content for the public About page (full hero section layout).
          </p>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Hero Background Image</label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleAboutHeroUpload}
                  disabled={aboutHeroUploading}
                  className="hidden"
                  id="about-hero-upload"
                />
                <label htmlFor="about-hero-upload" className="cursor-pointer flex flex-col items-center">
                  <Upload className="w-8 h-8 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600">
                    {aboutHeroUploading ? 'Uploading...' : 'Click to upload hero image'}
                  </span>
                  <span className="text-xs text-gray-500 mt-1">JPG, PNG, WebP (max 5MB)</span>
                </label>
              </div>
              {formData.about_hero_image && (
                <div className="mt-2 flex items-center gap-2">
                  <img src={formData.about_hero_image} alt="Hero preview" className="h-24 object-cover rounded" />
                  <button type="button" onClick={() => setFormData((prev) => ({ ...prev, about_hero_image: '' }))} className="text-xs text-red-600 hover:underline">Remove</button>
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle (small text above title)</label>
              <input
                type="text"
                value={formData.about_subtitle}
                onChange={(e) => setFormData({ ...formData, about_subtitle: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                placeholder="Who we are and what we do"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Main Title</label>
              <input
                type="text"
                value={formData.about_title}
                onChange={(e) => setFormData({ ...formData, about_title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                placeholder="ABOUT [YOUR COMPANY]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Content (paragraphs)</label>
              <textarea
                value={formData.about_content}
                onChange={(e) => setFormData({ ...formData, about_content: e.target.value })}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                placeholder="Your company story and description. Use line breaks for paragraphs."
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CTA Button Text</label>
                <input
                  type="text"
                  value={formData.about_cta_text}
                  onChange={(e) => setFormData({ ...formData, about_cta_text: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                  placeholder="Contact Us"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CTA Button Link</label>
                <input
                  type="text"
                  value={formData.about_cta_link}
                  onChange={(e) => setFormData({ ...formData, about_cta_link: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                  placeholder="/contact"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer Settings */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Footer Settings
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Footer Tagline
              </label>
              <textarea
                value={formData.footer_tagline}
                onChange={(e) => setFormData({ ...formData, footer_tagline: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                placeholder="Your inspiring tagline here..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Footer Copyright Text
              </label>
              <input
                type="text"
                value={formData.footer_copyright}
                onChange={(e) => setFormData({ ...formData, footer_copyright: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                placeholder="© 2024 Your Company. All rights reserved."
              />
            </div>
          </div>
        </div>

        {/* Social Media Links - Add/Remove dynamically */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <Share2 className="w-5 h-5" />
              Social Media Links
            </h2>
            <button
              type="button"
              onClick={addSocialLink}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Link
            </button>
          </div>
          <div className="space-y-4">
            {socialLinks.map((link) => (
              <div key={link.id} className="flex flex-col sm:flex-row gap-3 p-4 border border-gray-200 rounded-lg bg-gray-50/50">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Platform name</label>
                  <input
                    type="text"
                    value={link.platform}
                    onChange={(e) => updateSocialLink(link.id, 'platform', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black text-sm"
                    placeholder="e.g. Facebook, Instagram, Threads"
                  />
                </div>
                <div className="flex-[2]">
                  <label className="block text-xs font-medium text-gray-600 mb-1">URL</label>
                  <input
                    type="url"
                    value={link.url}
                    onChange={(e) => updateSocialLink(link.id, 'url', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black text-sm"
                    placeholder="https://..."
                  />
                </div>
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={() => removeSocialLink(link.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                    title="Remove"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* SEO Settings */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Eye className="w-5 h-5" />
            SEO & Analytics
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Meta Description
              </label>
              <textarea
                value={formData.meta_description}
                onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                placeholder="Brief description of your site for search engines (150-160 characters)"
                maxLength={160}
              />
              <p className="mt-1 text-xs text-gray-500">{formData.meta_description?.length || 0}/160 characters</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Meta Keywords
              </label>
              <input
                type="text"
                value={formData.meta_keywords}
                onChange={(e) => setFormData({ ...formData, meta_keywords: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                placeholder="keyword1, keyword2, keyword3"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Google Analytics ID
                </label>
                <input
                  type="text"
                  value={formData.google_analytics_id}
                  onChange={(e) => setFormData({ ...formData, google_analytics_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                  placeholder="G-XXXXXXXXXX"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Facebook Pixel ID
                </label>
                <input
                  type="text"
                  value={formData.facebook_pixel_id}
                  onChange={(e) => setFormData({ ...formData, facebook_pixel_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                  placeholder="123456789012345"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Custom Code */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Code className="w-5 h-5" />
            Custom Code
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Custom CSS
              </label>
              <textarea
                value={formData.custom_css}
                onChange={(e) => setFormData({ ...formData, custom_css: e.target.value })}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black font-mono text-sm"
                placeholder="/* Your custom CSS here */"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Custom JavaScript
              </label>
              <textarea
                value={formData.custom_js}
                onChange={(e) => setFormData({ ...formData, custom_js: e.target.value })}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black font-mono text-sm"
                placeholder="// Your custom JavaScript here"
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end pt-4 border-t">
          <Button
            type="submit"
            disabled={saveMutation.isPending}
            className="bg-black text-white hover:bg-gray-800"
          >
            <Save className="w-4 h-4 mr-2" />
            {saveMutation.isPending ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default SiteSettings;

